import { createAppKit, type AppKit } from "@reown/appkit";
import { EthersAdapter } from "@reown/appkit-adapter-ethers";
import {
  arbitrum,
  base,
  bsc,
  mainnet,
  polygon,
  polygonAmoy,
  sepolia,
  type AppKitNetwork,
} from "@reown/appkit/networks";
import { Interface } from "ethers";

const ERC20_APPROVE = new Interface([
  "function approve(address spender, uint256 amount) returns (bool)",
]);

const NETWORKS: [AppKitNetwork, ...AppKitNetwork[]] = [
  sepolia,
  polygonAmoy,
  polygon,
  mainnet,
  bsc,
  arbitrum,
  base,
];

const ADD_CHAIN: Record<number, { chainName: string; rpcUrls: string[]; nativeCurrency: { name: string; symbol: string; decimals: number }; blockExplorerUrls: string[] }> = {
  11155111: {
    chainName: "Sepolia",
    rpcUrls: ["https://ethereum-sepolia-rpc.publicnode.com"],
    nativeCurrency: { name: "Sepolia ETH", symbol: "ETH", decimals: 18 },
    blockExplorerUrls: ["https://sepolia.etherscan.io"],
  },
  80002: {
    chainName: "Polygon Amoy",
    rpcUrls: ["https://rpc-amoy.polygon.technology"],
    nativeCurrency: { name: "POL", symbol: "POL", decimals: 18 },
    blockExplorerUrls: ["https://amoy.polygonscan.com"],
  },
  137: {
    chainName: "Polygon",
    rpcUrls: ["https://polygon-rpc.com"],
    nativeCurrency: { name: "POL", symbol: "POL", decimals: 18 },
    blockExplorerUrls: ["https://polygonscan.com"],
  },
  1: {
    chainName: "Ethereum",
    rpcUrls: ["https://rpc.ankr.com/eth"],
    nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 },
    blockExplorerUrls: ["https://etherscan.io"],
  },
  56: {
    chainName: "BNB Smart Chain",
    rpcUrls: ["https://bsc-dataseed.binance.org"],
    nativeCurrency: { name: "BNB", symbol: "BNB", decimals: 18 },
    blockExplorerUrls: ["https://bscscan.com"],
  },
};

let modal: AppKit | null = null;
let appkitReady: Promise<AppKit> | null = null;
let connectInFlight: Promise<`0x${string}`> | null = null;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function clearStaleWalletConnectStorage(): Promise<void> {
  return new Promise((resolve) => {
    let settled = false;
    const done = () => {
      if (settled) return;
      settled = true;
      resolve();
    };
    try {
      const req = indexedDB.deleteDatabase("WALLET_CONNECT_V2_INDEXED_DB");
      req.onsuccess = done;
      req.onerror = done;
      req.onblocked = done;
      window.setTimeout(done, 800);
    } catch {
      done();
    }
  });
}

function projectId(): string {
  const id = import.meta.env.VITE_WALLETCONNECT_PROJECT_ID;
  if (!id) {
    throw new Error("Set VITE_WALLETCONNECT_PROJECT_ID");
  }
  return id;
}

function networkById(chainId: number): AppKitNetwork | undefined {
  return NETWORKS.find((net) => Number(net.id) === chainId);
}

function hexChainId(chainId: number): `0x${string}` {
  return `0x${chainId.toString(16)}`;
}

function providerErrorCode(error: unknown): number | undefined {
  if (!error || typeof error !== "object") return undefined;
  const rec = error as { code?: unknown; data?: { originalError?: { code?: unknown } } };
  const raw = rec.code ?? rec.data?.originalError?.code;
  return typeof raw === "number" ? raw : undefined;
}

export function getAppKit(defaultNetwork?: AppKitNetwork): AppKit {
  if (modal) return modal;
  const origin = window.location.origin;
  modal = createAppKit({
    adapters: [new EthersAdapter()],
    networks: NETWORKS,
    defaultNetwork: defaultNetwork || sepolia,
    defaultAccountTypes: { eip155: "eoa" },
    projectId: projectId(),
    metadata: {
      name: "Flowgate Recurring",
      description: "Approve recurring crypto charges",
      url: origin,
      icons: [`${origin}/logo.svg`],
    },
    themeMode: window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light",
    themeVariables: {
      "--apkt-font-family": "Inter, ui-sans-serif, system-ui, sans-serif",
      "--apkt-accent": "#8b5cf6",
      "--w3m-accent": "#8b5cf6",
    },
    features: {
      analytics: false,
      email: false,
      socials: false,
      send: false,
      swaps: false,
      onramp: false,
    },
    enableReconnect: false,
  });
  return modal;
}

function currentAddress(appkit: AppKit): `0x${string}` | null {
  const fromAccount = appkit.getAccount("eip155")?.address;
  const fromGetter = appkit.getAddress("eip155");
  const value = fromAccount || fromGetter;
  return value && value.startsWith("0x") ? (value as `0x${string}`) : null;
}

function isConnected(appkit: AppKit): boolean {
  return Boolean(appkit.getAccount("eip155")?.isConnected || appkit.getIsConnectedState() || currentAddress(appkit));
}

function evmProvider(appkit: AppKit): any {
  return appkit.getWalletProvider();
}

async function requestProvider(provider: any, method: string, params?: unknown): Promise<any> {
  if (!provider || typeof provider.request !== "function") {
    throw new Error("Wallet provider is not ready");
  }
  return provider.request(params === undefined ? { method } : { method, params });
}

async function readChainId(provider: any): Promise<number | null> {
  try {
    const raw = await requestProvider(provider, "eth_chainId");
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

async function waitForChain(provider: any, chainId: number, timeoutMs = 25_000): Promise<boolean> {
  const started = Date.now();
  while (Date.now() - started < timeoutMs) {
    if ((await readChainId(provider)) === chainId) return true;
    await new Promise((resolve) => window.setTimeout(resolve, 350));
  }
  return (await readChainId(provider)) === chainId;
}

async function addAndSwitchChain(provider: any, chainId: number, label: string): Promise<void> {
  const hex = hexChainId(chainId);
  const extra = ADD_CHAIN[chainId];
  try {
    await requestProvider(provider, "wallet_switchEthereumChain", [{ chainId: hex }]);
  } catch (error) {
    const code = providerErrorCode(error);
    if (code === 4001) {
      throw new Error(`Switch the wallet to ${label} and try again`);
    }
    if (extra && (code === 4902 || code === -32603 || String((error as Error)?.message || "").toLowerCase().includes("unrecognized"))) {
      await requestProvider(provider, "wallet_addEthereumChain", [
        {
          chainId: hex,
          chainName: extra.chainName,
          nativeCurrency: extra.nativeCurrency,
          rpcUrls: extra.rpcUrls,
          blockExplorerUrls: extra.blockExplorerUrls,
        },
      ]);
    } else {
      throw error;
    }
  }
}

async function ensureChain(appkit: AppKit, provider: any, network: AppKitNetwork): Promise<void> {
  const chainId = Number(network.id);
  const label = network.name || `chain ${chainId}`;
  if ((await readChainId(provider)) === chainId) return;

  try {
    await appkit.switchNetwork(network, { throwOnFailure: false });
  } catch {
    /* WalletConnect MetaMask often ignores AppKit switch; fall through to wallet_switchEthereumChain */
  }
  if (await waitForChain(provider, chainId, 4_000)) return;

  await addAndSwitchChain(provider, chainId, label);
  if (!(await waitForChain(provider, chainId))) {
    throw new Error(`Wallet stayed on the wrong network. Switch to ${label} in MetaMask, then retry.`);
  }
}

function modalState(appkit: AppKit) {
  return appkit.getState();
}

async function waitUntilDisconnected(appkit: AppKit, timeoutMs = 2_500): Promise<void> {
  const started = Date.now();
  while (Date.now() - started < timeoutMs) {
    if (!isConnected(appkit) && !currentAddress(appkit) && !modalState(appkit)?.open) {
      return;
    }
    await sleep(80);
  }
}

async function ensureAppKit(defaultNetwork?: AppKitNetwork): Promise<AppKit> {
  if (appkitReady) return appkitReady;
  appkitReady = (async () => {
    // Drop leftover WC sessions before AppKit boots. Otherwise UniversalProvider
    // restores the last pairing, the wallet popup flashes, then enableReconnect:false
    // disconnects it.
    if (!modal) await clearStaleWalletConnectStorage();
    const appkit = getAppKit(defaultNetwork);
    if (typeof appkit.ready === "function") await appkit.ready();
    const started = Date.now();
    while (!modalState(appkit)?.initialized && Date.now() - started < 10_000) {
      await sleep(50);
    }
    if (modalState(appkit)?.open && !isConnected(appkit) && !currentAddress(appkit)) {
      try {
        appkit.close();
      } catch {
        /* leftover ConnectingWalletConnect from init */
      }
      await waitUntilDisconnected(appkit, 1_500);
    }
    return appkit;
  })();
  try {
    return await appkitReady;
  } catch (error) {
    appkitReady = null;
    throw error;
  }
}

async function openConnectModal(appkit: AppKit): Promise<void> {
  await appkit.open({ view: "Connect", namespace: "eip155" });
  await sleep(120);
  if (!modalState(appkit)?.open && !isConnected(appkit) && !currentAddress(appkit)) {
    await appkit.open({ view: "Connect", namespace: "eip155" });
  }
}

function waitForProvider(appkit: AppKit): Promise<any> {
  const current = evmProvider(appkit);
  if (current) return Promise.resolve(current);
  return new Promise((resolve, reject) => {
    const timeout = window.setTimeout(() => {
      unsub();
      reject(new Error("Wallet provider is not ready"));
    }, 15_000);
    const unsub = appkit.subscribeProviders((state) => {
      const next = state["eip155"] || evmProvider(appkit);
      if (next) {
        window.clearTimeout(timeout);
        unsub();
        resolve(next);
      }
    });
  });
}

async function waitForConnection(appkit: AppKit): Promise<`0x${string}`> {
  const existing = currentAddress(appkit);
  if (existing && isConnected(appkit)) return existing;

  return new Promise((resolve, reject) => {
    let settled = false;
    let sawStableOpen = false;
    let openedAt = 0;
    let closeTimer = 0;
    const finish = (fn: () => void) => {
      if (settled) return;
      settled = true;
      if (closeTimer) window.clearTimeout(closeTimer);
      unsubAcc();
      unsubState();
      fn();
    };

    const stillConnecting = (state = modalState(appkit)) =>
      Boolean(
        state?.open ||
          state?.loading ||
          state?.connectingWallet ||
          !state?.initialized ||
          isConnected(appkit) ||
          currentAddress(appkit)
      );

    const unsubAcc = appkit.subscribeAccount((state) => {
      const address = state.address;
      if (state.isConnected && address?.startsWith("0x")) {
        finish(() => resolve(address as `0x${string}`));
      }
    }, "eip155");

    const unsubState = appkit.subscribeState((state) => {
      if (state.open && state.initialized && !state.loading) {
        if (!openedAt) openedAt = Date.now();
        if (Date.now() - openedAt < 400) return;
        sawStableOpen = true;
        if (closeTimer) {
          window.clearTimeout(closeTimer);
          closeTimer = 0;
        }
        return;
      }
      openedAt = 0;
      // Init leftover and Connect → wallet view swaps briefly close the modal.
      if (!sawStableOpen || stillConnecting(state)) {
        if (closeTimer) {
          window.clearTimeout(closeTimer);
          closeTimer = 0;
        }
        return;
      }
      if (closeTimer) window.clearTimeout(closeTimer);
      closeTimer = window.setTimeout(() => {
        if (stillConnecting()) return;
        finish(() => reject(new Error("Wallet connection cancelled")));
      }, 1_200);
    });
  });
}

export function prefetchWallet(): void {
  void ensureAppKit().catch(() => {
    /* project id / AppKit can fail until subscribe */
  });
}

export async function connectWallet(chainId: number): Promise<`0x${string}`> {
  const network = networkById(chainId);
  if (!network) {
    throw new Error(`Unsupported chain ${chainId}`);
  }
  const appkit = await ensureAppKit(network);
  const existing = currentAddress(appkit);
  if (existing && isConnected(appkit)) return existing;
  if (connectInFlight) return connectInFlight;

  connectInFlight = (async () => {
    const pending = waitForConnection(appkit);
    await openConnectModal(appkit);
    try {
      return await pending;
    } catch (error) {
      connectInFlight = null;
      throw error;
    }
  })();
  return connectInFlight;
}

export async function connectAndApprove(opts: {
  chainId: number;
  token: string;
  spender: string;
  amountRaw: bigint;
}): Promise<{ payer: `0x${string}`; tx: string }> {
  const network = networkById(opts.chainId);
  if (!network) {
    throw new Error(`Unsupported chain ${opts.chainId}`);
  }
  const appkit = await ensureAppKit(network);
  const payer = await connectWallet(opts.chainId);
  const provider = evmProvider(appkit) || (await waitForProvider(appkit));
  if (!payer || !provider) {
    throw new Error("Wallet is not connected");
  }

  await ensureChain(appkit, provider, network);

  const data = ERC20_APPROVE.encodeFunctionData("approve", [opts.spender, opts.amountRaw]);
  const hash = await requestProvider(provider, "eth_sendTransaction", [
    {
      from: payer,
      to: opts.token,
      data,
      value: "0x0",
      chainId: hexChainId(opts.chainId),
    },
  ]);
  if (typeof hash !== "string" || !hash.startsWith("0x")) {
    throw new Error("Wallet did not return a transaction hash");
  }
  connectInFlight = null;
  appkit.close();
  return { payer, tx: hash };
}
